import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import _ from 'lodash';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class TopicvizService {
  constructor(private http: HttpClient) {}

  resetAll() {
    return this.http.get(`${environment.topicVizApi}/reset_database`, {
      params: {},
    });
  }

  deleteCluster({ spatialCollectionId = null }) {
    const param: any = {
      spatial_collection_id: spatialCollectionId,
    };

    return this.http.get(`${environment.topicVizApi}/clusters/delete`, {
      params: param,
    });
  }

  deleteDataset(name) {
    return this.http.get(`${environment.topicVizApi}/datasets/delete`, {
      params: { name: name },
    });
  }

  loadDocumentInfo(id, size, photoType) {
    return this.http.get(`${environment.topicVizApi}/documents/info`, {
      params: { id: id, photoType: photoType, size: size },
    });
  }

  // allows to override cluster keywords and cluster pictures
  updateClusterMetaData({
    clusteredCollectionId,
    overrideWords,
    overrideUrls,
    topicIndex,
  }) {
    const body: any = {
      clustered_collection_id: clusteredCollectionId,
      override_words: overrideWords,
      override_urls: overrideUrls,
      topic_index: topicIndex,
    };

    return this.http.post(
      `${environment.topicVizApi}/clusters/update_topic_keywords`,
      body,
    );
  }

  getClusterConfigurations() {
    return this.http.get(`${environment.topicVizApi}/clusters/configurations`);
  }

  getClusterInfo(
    sourceDatasetName,
    targetDatasetName: string,
    clusteringAlgorithm: string,
    clusterSize: number,
    imageType: string,
    spatialCollectionId: number,
  ): Observable<any> {
    let param: any = {
      target_dataset: targetDatasetName,
      source_dataset: sourceDatasetName,
      clustering_algorithm: clusteringAlgorithm,
      cluster_size: clusterSize,
      image_type: imageType,
      spatial_collection_id: spatialCollectionId,
    };

    param = _.pickBy(param, _.identity);

    return this.http.get(`${environment.topicVizApi}/clusters/topic_data`, {
      params: param,
    });
  }

  getAvailableDatasets() {
    return this.http.get(`${environment.topicVizApi}/datasets/available`);
  }

  loadAllDatasets() {
    return this.http.get(`${environment.topicVizApi}/datasets/all`);
  }

  getWordLists() {
    return this.http.get(`${environment.topicVizApi}/datasets/word_lists`);
  }

  setDefaultCluster({
    sourceDatasetName = null,
    targetDatasetName = null,
    clusteringAlgorithm = null,
    clusterSize = null,
    reductionAlgorithm = null,
  }) {
    const param: any = {
      source_dataset: sourceDatasetName,
      target_dataset: targetDatasetName,
      clustering_algorithm: clusteringAlgorithm,
      cluster_size: clusterSize,
      dimensionality_reduction: reductionAlgorithm,
    };

    return this.http.get(`${environment.topicVizApi}/clusters/set_default`, {
      params: param,
    });
  }

  computeCluster({
    sourceDatasetName = null,
    targetDatasetName = null,
    clusteringAlgorithm = null,
    clusterSize = null,
    reductionAlgorithm = null,
    wordList = null,
    additionalDataset = null,
  }): Observable<any> {
    const param: any = {
      source_dataset: sourceDatasetName,
      target_dataset: targetDatasetName,
      clustering_algorithm: clusteringAlgorithm,
      cluster_size: clusterSize,
      dimensionality_reduction: reductionAlgorithm,
      word_list: wordList,
      additional_dataset: additionalDataset,
    };

    return this.http.get(`${environment.topicVizApi}/clusters/compute`, {
      params: param,
    });
  }

  getDocumentData(
    sourceDatasetName,
    targetDatasetName: string,
    clusteringAlgorithm: string,
    countClusters: number,
    reductionAlgorithm: string,
    imageType: string,
    spatialCollectionId: number,
  ) {
    let param: any = {
      spatial_collection_id: spatialCollectionId,
      source_dataset: sourceDatasetName,
      target_dataset: targetDatasetName,
      clustering_algorithm: clusteringAlgorithm,
      cluster_size: countClusters,
      dimensionality_reduction: reductionAlgorithm,
      image_type: imageType,
    };

    param = _.pickBy(param, _.identity);

    return this.http.get(`${environment.topicVizApi}/clusters/document_data`, {
      params: param,
    });
  }

  importWordList(wordListAsCsv: string, name: string) {
    return this.http.post(`${environment.topicVizApi}/import/word_list`, {
      name: name,
      csv: wordListAsCsv,
    });
  }

  importDataset(
    datasetAsCsv: string,
    name: string,
    algorithmName: string,
    nKeywords,
    findPictures,
  ) {
    return this.http.post(`${environment.topicVizApi}/import/dataset`, {
      name: name,
      csv: datasetAsCsv,
      algorithm_name: algorithmName,
      n_keywords: nKeywords,
      find_pictures: findPictures,
    });
  }

  syncIdeas(algorithmName, nKeywords) {
    return this.http.get(`${environment.topicVizApi}/import/sync_ideas`, {
      params: {
        n_keywords: nKeywords,
        algorithm_name: algorithmName,
      },
    });
  }

  getImageStatus() {
    return this.http.get(`${environment.topicVizApi}/images/status`);
  }
}
